import React, { useState, useEffect } from 'react';
import '../styles/clean-dashboard.css';
import DeviceManager from './DeviceManager';
import UserManager from './UserManager';
import Chat from './Chat';
import APIDocumentation from './APIDocumentation';
import { getApiBaseUrl } from '../config/api';

const Dashboard = ({ user, onLogout, onOpenChat }) => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [instances, setInstances] = useState([]);
  const [selectedInstance, setSelectedInstance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [stats, setStats] = useState({
    totalDevices: 0,
    connectedDevices: 0,
    totalMessages: 0,
    activeUsers: 0
  });

  useEffect(() => {
    fetchInstances();
    fetchDashboardStats();
  }, []);

  const fetchInstances = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      console.log('Fetching instances with token:', token ? 'Present' : 'Missing');
      
      const response = await fetch(getApiBaseUrl('/instances'), {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      console.log('Response status:', response.status);
      const data = await response.json();
      console.log('Response data:', data);
      
      if (data.success) {
        console.log('Setting instances:', data.instances || []);
        setInstances(data.instances || []);
        
        // Calculate stats from instances
        const devices = data.instances || [];
        const connected = devices.filter(d => d.status === 'connected').length;
        setStats(prev => ({
          ...prev,
          totalDevices: devices.length,
          connectedDevices: connected
        }));
      } else {
        console.error('API error:', data.error);
      }
    } catch (error) {
      console.error('Failed to fetch instances:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDashboardStats = async () => {
    try {
      // You can implement these endpoints in your backend
      // For now, we'll use mock data
      setStats(prev => ({
        ...prev,
        totalMessages: 1250,
        activeUsers: 8
      }));
    } catch (error) {
      console.error('Failed to fetch dashboard stats:', error);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user_data');
    onLogout();
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    closeMobileMenu();
  };

  const tabs = [
    { id: 'dashboard', label: 'Dashboard', icon: '📊' },
    { id: 'devices', label: 'Devices', icon: '📱' },
    { id: 'chat', label: 'Chat', icon: '💬' },
    { id: 'api-docs', label: 'API Documentation', icon: '📚' },
  ];

  // Add admin tabs if user is admin or super_admin
  if (user.role === 'admin' || user.role === 'super_admin') {
    tabs.push({ id: 'users', label: 'Users', icon: '👥' });
  }

  return (
    <div className="clean-dashboard">
      {/* Mobile Header */}
      <div className="mobile-header">
        <div className="mobile-logo">
          <div className="brand-icon">WA</div>
          <span className="brand-text">WhatsApp Manager</span>
        </div>
        <button className="mobile-menu-toggle" onClick={toggleMobileMenu}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <line x1="3" y1="6" x2="21" y2="6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            <line x1="3" y1="12" x2="21" y2="12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            <line x1="3" y1="18" x2="21" y2="18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          </svg>
        </button>
      </div>

      {/* Mobile Sidebar Overlay */}
      <div className={`mobile-sidebar-overlay ${mobileMenuOpen ? 'show' : ''}`} onClick={closeMobileMenu}></div>

      <div className={`clean-sidebar ${mobileMenuOpen ? 'show' : ''}`}>
        <div className="sidebar-brand">
          <div className="brand-icon">
            WA
          </div>
          <div className="brand-text">WhatsApp Manager</div>
        </div>
        
        <nav className="sidebar-nav">
          {tabs.map(tab => (
            <button
              key={tab.id}
              className={`nav-item ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => handleTabChange(tab.id)}
            >
              <span className="nav-icon">{tab.icon}</span>
              <span className="nav-label">{tab.label}</span>
            </button>
          ))}
        </nav>
        
        <div className="sidebar-user">
          <div className="user-profile">
            <div className="user-avatar">
              {user.username.charAt(0).toUpperCase()}
            </div>
            <div className="user-info">
              <div className="user-name">{user.username}</div>
              <div className="user-role">{user.role.replace('_', ' ')}</div>
            </div>
          </div>
          <button onClick={handleLogout} className="logout-btn">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <polyline points="16,17 21,12 16,7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <line x1="21" y1="12" x2="9" y2="12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Logout
          </button>
        </div>
      </div>

      <div className="dashboard-main">
        {loading ? (
          <div className="loading-spinner">
            <div className="spinner"></div>
            <p>Loading...</p>
          </div>
        ) : (
          <>
            {activeTab === 'dashboard' && (
              <div className="dashboard-overview">
                <div className="dashboard-header">
                  <div className="header-content">
                    <h1 className="header-title">Dashboard Overview</h1>
   
                  </div>
                </div>

                <div className="stats-grid">
                  <div className="stat-card">
                    <div className="stat-icon">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 2L2 7V10C2 16 6 20.5 12 22C18 20.5 22 16 22 10V7L12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                    <div className="stat-content">
                      <div className="stat-number">{stats.totalDevices}</div>
                      <div className="stat-label">Total Devices</div>
                    </div>
                  </div>

                  <div className="stat-card">
                    <div className="stat-icon connected">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                        <polyline points="8,12 11,15 16,10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                    <div className="stat-content">
                      <div className="stat-number">{stats.connectedDevices}</div>
                      <div className="stat-label">Connected</div>
                    </div>
                  </div>

                  <div className="stat-card">
                    <div className="stat-icon">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                    <div className="stat-content">
                      <div className="stat-number">{stats.totalMessages}</div>
                      <div className="stat-label">Messages Sent</div>
                    </div>
                  </div>

                  <div className="stat-card">
                    <div className="stat-icon">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <circle cx="12" cy="7" r="4" stroke="currentColor" strokeWidth="2"/>
                      </svg>
                    </div>
                    <div className="stat-content">
                      <div className="stat-number">{stats.activeUsers}</div>
                      <div className="stat-label">Active Users</div>
                    </div>
                  </div>
                </div>

                <div className="dashboard-content-grid">
                  <div className="activity-section">
                    <div className="section-header">
                      <h3>Recent Activity</h3>
                    </div>
                    <div className="activity-list">
                      <div className="activity-item">
                        <div className="activity-icon connected">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                            <polyline points="8,12 11,15 16,10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </div>
                        <div className="activity-content">
                          <div className="activity-title">Device Connected</div>
                          <div className="activity-desc">WhatsApp Business API connected successfully</div>
                          <div className="activity-time">2 minutes ago</div>
                        </div>
                      </div>

                      <div className="activity-item">
                        <div className="activity-icon">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </div>
                        <div className="activity-content">
                          <div className="activity-title">Message Sent</div>
                          <div className="activity-desc">Broadcast message delivered to 45 contacts</div>
                          <div className="activity-time">5 minutes ago</div>
                        </div>
                      </div>

                      <div className="activity-item">
                        <div className="activity-icon">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <circle cx="12" cy="7" r="4" stroke="currentColor" strokeWidth="2"/>
                          </svg>
                        </div>
                        <div className="activity-content">
                          <div className="activity-title">New User Added</div>
                          <div className="activity-desc">Admin user created with full permissions</div>
                          <div className="activity-time">15 minutes ago</div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="quick-actions-section">
                    <div className="section-header">
                      <h3>Quick Actions</h3>
                    </div>
                    <div className="quick-actions-grid">
                      <button className="quick-action-btn" onClick={() => handleTabChange('devices')}>
                        <div className="action-icon">
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <rect x="2" y="4" width="20" height="16" rx="2" stroke="currentColor" strokeWidth="2"/>
                            <path d="M10 4v16" stroke="currentColor" strokeWidth="2"/>
                          </svg>
                        </div>
                        <span>Manage Devices</span>
                      </button>

                      <button className="quick-action-btn" onClick={() => handleTabChange('users')}>
                        <div className="action-icon">
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <circle cx="9" cy="7" r="4" stroke="currentColor" strokeWidth="2"/>
                            <path d="M23 21v-2a4 4 0 0 0-3-3.87" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M16 3.13a4 4 0 0 1 0 7.75" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </div>
                        <span>Manage Users</span>
                      </button>

                      <button className="quick-action-btn" onClick={() => handleTabChange('chat')}>
                        <div className="action-icon">
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </div>
                        <span>WhatsApp Web</span>
                      </button>

                      <button className="quick-action-btn" onClick={() => handleTabChange('api-docs')}>
                        <div className="action-icon">
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <polygon points="13,2 3,14 12,14 11,22 21,10 12,10 13,2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </div>
                        <span>API Documentation</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'devices' && (
              <div className="page-content">
                <DeviceManager 
                  instances={instances}
                  onInstancesChange={fetchInstances}
                  onSelectInstance={setSelectedInstance}
                  onOpenChat={onOpenChat}
                  user={user}
                />
              </div>
            )}
            
            {activeTab === 'chat' && (
              <div className="page-content">
                <Chat 
                  instances={instances}
                  selectedInstance={selectedInstance}
                  onSelectInstance={setSelectedInstance}
                />
              </div>
            )}
            
            {activeTab === 'users' && (user.role === 'admin' || user.role === 'super_admin') && (
              <div className="page-content">
                <UserManager user={user} />
              </div>
            )}
            
            {activeTab === 'api-docs' && (
              <div className="page-content">
                <APIDocumentation />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
